import React, { FC, useEffect, useState } from 'react'

import { Grid, Theme, useMediaQuery, useTheme } from '@mui/material'
import { isEmpty, isEqual } from 'lodash'
import { useTranslation } from 'react-i18next'

import { LevelOfStressWidget } from '../../../components/ReportsWidgets'
import { PopularityWidget } from '../../../components/ReportsWidgets/PopularityWidget'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { getNormalizedBarData } from '../../../utils'
import { getJournalingOptionTranslationKey } from '../../TradingLog/tradingLogLabels'
import { fetchTradingConfluenceReport } from '../ReportsSlice'

import { SQNWidget } from './SQNWidget'

export const TradingConfluenceReports: FC = () => {
  const { palette } = useTheme()
  const { t } = useTranslation()
  const matchesBreakpoints: boolean = useMediaQuery((theme: Theme) => theme.breakpoints.up(2000))
  const dispatch = useAppDispatch()

  const { period, currentAccount } = useAppSelector(({ userAccounts }) => userAccounts)
  const { tradingConfluence } = useAppSelector(({ reports }) => reports)
  const { mostPopularProfitable, topByLevelOfStress, topBySystemQualityNumber, meta } =
    tradingConfluence || {}
  const [displayWidth, setDisplayWidth] = useState(window.innerWidth)

  useEffect(() => {
    const periodsIsEqual = meta?.period && period && isEqual(meta.period, period)
    const isNeedToUpdate =
      isEmpty(tradingConfluence) || !periodsIsEqual || currentAccount?.id !== meta.account

    if (currentAccount?.id && isNeedToUpdate) {
      dispatch(
        fetchTradingConfluenceReport({
          account: currentAccount?.id,
          period: period,
        }),
      )
    }
  }, [period, currentAccount])

  const popularData = getNormalizedBarData<'name'>(mostPopularProfitable, 'name', 'amount', 10).map(
    (item) => ({
      ...item,
      parameter: t(
        `tradingLogPage.journaling.options.${getJournalingOptionTranslationKey(
          item.parameter,
          'tradingConfluence',
        )}`,
        item.parameter,
      ),
    }),
  )

  const profitableData = getNormalizedBarData<'name'>(mostPopularProfitable, 'name', 'profit', 10)
    .map((item) => ({
      ...item,
      parameter: t(
        `tradingLogPage.journaling.options.${getJournalingOptionTranslationKey(
          item.parameter,
          'tradingConfluence',
        )}`,
        item.parameter,
      ),
    }))
    .filter((bar) => bar.value >= 0)

  const stressData = getNormalizedBarData<'name'>(topByLevelOfStress, 'name', 'perCent', 10).map(
    (item) => ({
      ...item,
      parameter: t(
        `tradingLogPage.journaling.options.${getJournalingOptionTranslationKey(
          item.parameter,
          'tradingConfluence',
        )}`,
        item.parameter,
      ),
    }),
  )

  const localizedSqnData = topBySystemQualityNumber?.map((item) => ({
    ...item,
    symbol: t(
      `tradingLogPage.journaling.options.${getJournalingOptionTranslationKey(
        item.symbol,
        'tradingConfluence',
      )}`,
      item.symbol,
    ),
  }))

  useEffect(() => {
    const handleResize = () => {
      setDisplayWidth(window.innerWidth)
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <Grid container spacing={2}>
      <Grid
        item
        lg={matchesBreakpoints ? 4 : 6}
        xs={12}
        sm={12}
        md={6}
        sx={{ pageBreakInside: 'avoid' }}
      >
        <PopularityWidget
          title={t('mostPopularTradingConfluence')}
          explanation={t('theGraphRepresentsWhatAreTheMostPopularConfluenceAreSelected')}
          data={popularData}
          mainColor={palette.blue[500] as string}
          strategies
        />
      </Grid>
      <Grid
        item
        lg={matchesBreakpoints ? 4 : 6}
        xs={12}
        sm={12}
        md={6}
        sx={{ pageBreakInside: 'avoid' }}
      >
        <PopularityWidget
          title={t('mostProfitableTradingConfluence')}
          explanation={t('theGraphRepresentsWhatAreTheMostProfitableConfluenceHaveMostProfit')}
          data={profitableData}
          mainColor={palette.green[400] as string}
          strategies
          currency={currentAccount?.currency}
        />
      </Grid>
      <Grid
        item
        lg={matchesBreakpoints ? 4 : 6}
        xs={12}
        sm={12}
        md={6}
        sx={{ pageBreakInside: 'avoid' }}
      >
        <LevelOfStressWidget
          title={t('topConfluenceByLevelOfStress')}
          explanation={t('theGraphRepresentsWhatConfluencesTheMostStressful')}
          data={stressData}
          minZone={30}
          maxZone={60}
          strategies
        />
      </Grid>

      <Grid
        item
        lg={matchesBreakpoints ? 4 : 6}
        xs={12}
        sm={12}
        md={6}
        sx={{ pageBreakInside: 'avoid' }}
      >
        <SQNWidget sqnData={localizedSqnData} displayWidth={displayWidth} />
      </Grid>
    </Grid>
  )
}
